/**
 * InstallerOffersCockpit — canonical CCC command surface for the
 * installer-offer workflow.
 *
 * Workflow centered:
 *   1. Job ready for installer network
 *   2. CCC sets Offer Compensation
 *   3. CCC sends installer offer
 *   4. Installer accepts / declines
 *   5. Execution lifecycle begins
 *
 * Route data is server-paged through v_installer_offer_* views so this cockpit
 * does not hydrate the full jobs, marketplace, contractor, inventory, or
 * onboarding datasets just to render tab counts and rows.
 *
 * Financial visibility: NEVER exposes `Job.financials.contractValue`,
 * margin, or customer economics — only installer compensation
 * (`payoutAmount` / `payoutAmountSnapshot`).
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import { useInstallerOffersServerData } from "@/hooks/useInstallerOffersServerPaging";
import { INSTALLER_OFFERS_SERVER_PAGING_ENABLED } from "@/lib/dataRuntimeFlags";
import type {
  InstallerOfferJobRow,
  InstallerOfferRow,
  InstallerOfferTab,
} from "@/services/installer-offers-page.service";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import {
  CockpitTabs,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  StatusDot,
  CockpitSearch,
} from "@/components/cockpit/CockpitShell";
import { PaginationBar } from "@/components/PaginationBar";
import { Button } from "@/components/ui/button";
import { displayIdentity } from "@/lib/uuid";

const TAB_LABELS: Record<InstallerOfferTab, string> = {
  ready: "Ready to Offer",
  needs_comp: "Needs Compensation",
  offered: "Offered",
  accepted: "Accepted",
  declined: "Declined / Expired",
};

const RECENT_WINDOW_DAYS = 14;
const INSTALLER_OFFERS_TIME_ZONE = "America/New_York";

function jobDisplayRef(job: Pick<InstallerOfferJobRow | InstallerOfferRow, "jobNumber" | "id">): string {
  return displayIdentity(job.jobNumber || job.id, "JOB");
}

function isJobRow(row: InstallerOfferJobRow | InstallerOfferRow): row is InstallerOfferJobRow {
  return row.offerTab === "ready" || row.offerTab === "needs_comp";
}

function isOfferRow(row: InstallerOfferJobRow | InstallerOfferRow): row is InstallerOfferRow {
  return !isJobRow(row);
}

function formatOfferDate(value: string): string {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString(undefined, { timeZone: INSTALLER_OFFERS_TIME_ZONE });
}

export default function InstallerOffersCockpit() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState<InstallerOfferTab>("ready");
  const pagination = useCountedServerPagination(20);
  const { reset, setTotalItems } = pagination;
  const serverData = useInstallerOffersServerData({
    enabled: INSTALLER_OFFERS_SERVER_PAGING_ENABLED,
    page: pagination.page,
    pageSize: pagination.pageSize,
    search,
    tab,
  });

  useEffect(() => {
    reset();
  }, [reset, search, tab]);

  useEffect(() => {
    setTotalItems(serverData.count);
  }, [serverData.count, setTotalItems]);

  const counts: Record<InstallerOfferTab, number> = {
    ready: serverData.summary?.ready ?? 0,
    needs_comp: serverData.summary?.needsComp ?? 0,
    offered: serverData.summary?.offered ?? 0,
    accepted: serverData.summary?.accepted ?? 0,
    declined: serverData.summary?.declined ?? 0,
  };

  const tabsConfig = (Object.keys(TAB_LABELS) as InstallerOfferTab[]).map((id) => ({
    value: id,
    label: TAB_LABELS[id],
    count: counts[id],
  }));

  const rows = serverData.rows;
  const jobRows = useMemo(() => rows.filter(isJobRow), [rows]);
  const offerRows = useMemo(() => rows.filter(isOfferRow), [rows]);

  const renderJobRow = (job: InstallerOfferJobRow, variant: "ready" | "needs_comp") => {
    const compLabel = job.payoutAmount > 0 ? `$${job.payoutAmount.toLocaleString()}` : "—";
    const compTone: "good" | "warn" = job.payoutAmount > 0 ? "good" : "warn";

    return (
      <CockpitRow
        key={job.id}
        template="2fr 1.4fr 1fr 1.2fr 1.6fr"
        affordance={false}
      >
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {job.customerName || jobDisplayRef(job)}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {jobDisplayRef(job)} · {job.projectType}
          </div>
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {job.city || "—"}
        </div>
        <div className="flex items-center gap-1.5 text-xs tabular-nums">
          <StatusDot tone={compTone} />
          <span>{compLabel}</span>
        </div>
        <div className="text-xs tabular-nums text-muted-foreground">
          Eligible installers: {job.eligibleInstallerCount}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <Button
            size="sm"
            variant={variant === "ready" ? "default" : "outline"}
            onClick={() => navigate(`/installer-offers/${job.id}/review`)}
          >
            Review &amp; Offer
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/jobs/${job.id}`)}
          >
            View Job
          </Button>
        </div>
      </CockpitRow>
    );
  };

  const renderOfferRow = (offer: InstallerOfferRow) => {
    const snapshot = offer.payoutAmountSnapshot ?? 0;
    const tone: "good" | "warn" | "bad" | "muted" =
      offer.status === "accepted"
        ? "good"
        : offer.status === "pending"
          ? "warn"
          : offer.status === "declined" || offer.status === "expired"
            ? "bad"
            : "muted";

    return (
      <CockpitRow
        key={offer.id}
        template="2fr 1.4fr 1fr 1.2fr 1.6fr"
        affordance={false}
      >
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">
            {offer.customerName || jobDisplayRef(offer)}
          </div>
          <div className="text-[11px] text-muted-foreground truncate">
            {jobDisplayRef(offer)} · {offer.projectType}
          </div>
        </div>
        <div className="text-xs text-muted-foreground truncate">
          {offer.contractorName || offer.contractorId}
        </div>
        <div className="flex items-center gap-1.5 text-xs tabular-nums">
          <StatusDot tone={tone} />
          <span className="capitalize">{offer.status}</span>
        </div>
        <div className="text-xs tabular-nums text-muted-foreground">
          ${snapshot.toLocaleString()}
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <span className="text-[11px] text-muted-foreground">
            {formatOfferDate(offer.sentAt)}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/installer-offers/${offer.jobId}/review`)}
          >
            {offer.status === "accepted" ? "View / Update Comp" : "Review Offer"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => navigate(`/jobs/${offer.jobId}`)}
          >
            View Job
          </Button>
        </div>
      </CockpitRow>
    );
  };

  const headerColumns = (variant: "job" | "offer") => (
    <CockpitColumns template="2fr 1.4fr 1fr 1.2fr 1.6fr">
      <div>Job</div>
      <div>{variant === "job" ? "Location" : "Installer"}</div>
      <div>{variant === "job" ? "Compensation" : "Status"}</div>
      <div>{variant === "job" ? "Eligibility" : "Offer Amount"}</div>
      <div className="text-right">Actions</div>
    </CockpitColumns>
  );

  let body: React.ReactNode;
  if (!INSTALLER_OFFERS_SERVER_PAGING_ENABLED) {
    body = <CockpitEmpty message="Installer Offers server paging is disabled." />;
  } else if (tab === "ready" || tab === "needs_comp") {
    body = jobRows.length === 0 ? (
      <CockpitEmpty message={tab === "ready" ? "No jobs ready to offer right now." : "Every offerable job has compensation set."} />
    ) : (
      <>
        {headerColumns("job")}
        <CockpitListShell>
          {jobRows.map((job) => renderJobRow(job, tab))}
        </CockpitListShell>
        <PaginationBar {...pagination} />
      </>
    );
  } else {
    body = offerRows.length === 0 ? (
      <CockpitEmpty message={`No ${TAB_LABELS[tab].toLowerCase()} offers in the last ${RECENT_WINDOW_DAYS} days.`} />
    ) : (
      <>
        {headerColumns("offer")}
        <CockpitListShell>{offerRows.map(renderOfferRow)}</CockpitListShell>
        <PaginationBar {...pagination} />
      </>
    );
  }

  return (
    <CockpitPage
      eyebrow="Execution"
      title="Installer Offers"
      subtitle="Set offer compensation, send offers, and track installer acceptance for every job ready for the installer network."
      controls={
        <CockpitSearch value={search} onChange={setSearch} placeholder="Search jobs..." />
      }
      tabs={
        <CockpitTabs
          tabs={tabsConfig}
          value={tab}
          onChange={(value) => setTab(value as InstallerOfferTab)}
        />
      }
    >
      <div className="mt-4">{body}</div>
    </CockpitPage>
  );
}
